import React, { useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import Box from "Components/Atoms/Box";
import { DragIndicator } from "Components/Atoms/Icons";
import "./styles.scss";
import { Translations } from "utils/types";
import Card from "../Card";
import { noop } from "utils/helper";
import Row from "./Components/Row";
import Header from "./Components/Header";
import LoadingScreen from "Components/Atoms/LoadingScreen";
import { CircularProgress } from "@material-ui/core";
import EmptyState from "Components/Organisms/ShiftReservationCard/Components/EmptyState";
import Typography from "../../Atoms/Typography";

export type Column = {
  id: string;
  label: string;
  labelTranslation?: Translations;
  align?: "right" | "left" | "center" | any;
  minWidth?: number | string;
};

export type SimpleTableProps = {
  columns: Column[];
  selected?: string | null;
  rows: { id: any }[];
  onRowClick: (id: any) => void;
  hideBorders?: boolean;
  loading?: boolean;
  className?: string;
  style?: object;
  onReorder?: (ids: any[]) => void;
};

const SimpleTable = ({
  style,
  selected,
  className,
  columns = [],
  rows = [],
  onRowClick = noop,
  hideBorders = false,
  loading = false,
  onReorder,
}: SimpleTableProps) => {
  const output = useMemo(() => {
    let output: any = {};

    if (rows.length) {
      Object.keys(rows[0]).forEach((key) => {
        output[key] = rows.map((obj: any) => obj[key]);
      });
      Object.keys(rows[0]).forEach((key) => {
        output[key] = rows.map((obj: any) => obj[key]);
      });
    }
    return output;
  }, [rows]);

  const noData = rows.length === 0 && !loading;

  const { t } = useTranslation("common");

  // The parent re-renders only after the write is confirmed, so the dropped
  // order is held locally until the rows arrive in that order.
  const [pendingOrder, setPendingOrder] = useState<{
    rowIds: string;
    ids: any[];
  } | null>(null);
  const rowIds = rows.map((r) => r.id).join("\u0000");
  if (pendingOrder && pendingOrder.rowIds !== rowIds) setPendingOrder(null);
  const orderedRows =
    pendingOrder?.rowIds === rowIds
      ? pendingOrder.ids.map((id) => rows.find((r) => r.id === id))
      : rows;

  const renderRow = (r: { id: any }, i: number) => (
    <Row
      style={style}
      className={className}
      key={r.id ?? i}
      data={r}
      id={r.id}
      selected={selected === r.id}
      onClick={onRowClick}
      columns={columns}
      ContentWidth={output}
    />
  );

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (!onReorder || !destination || destination.index === source.index)
      return;
    const ids = orderedRows.map((r) => r.id);
    ids.splice(destination.index, 0, ids.splice(source.index, 1)[0]);
    setPendingOrder({ rowIds, ids });
    onReorder(ids);
  };

  return (
    <Card
      hideHeader
      hideBorders={hideBorders}
      subHeader={<Header columns={columns} ContentWidth={output} />}
      bodyProps={{ padding: 0 }}
      style={{ minWidth: "200px", overflowX: "auto" }}
      className="simple-table-wrapper"
    >
      {!loading && !onReorder && rows.map(renderRow)}
      {!loading && onReorder && (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="simple-table">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {orderedRows.map((r, i) => (
                  <Draggable key={r.id} draggableId={String(r.id)} index={i}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          position: "relative",
                          ...provided.draggableProps.style,
                          background: snapshot.isDragging
                            ? "var(--color-paper)"
                            : undefined,
                        }}
                      >
                        <span
                          {...provided.dragHandleProps}
                          aria-label={t("Drag to change the order")}
                          className="simple-table-drag-handle"
                        >
                          <DragIndicator style={{ fontSize: 16 }} />
                        </span>
                        {renderRow(r, i)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      {loading && (
        <Box flex fullSize className="center" style={{ minHeight: 200 }}>
          <CircularProgress />
        </Box>
      )}
      {noData && (
        <Box padding={24}>
          <EmptyState description="Currently, there is no data to show" />
        </Box>
      )}
    </Card>
  );
};

export default SimpleTable;
